<template>
	<div 
		class="component video-player" 
		:style="{ 
			maxHeight: (forceMaxHeight && !isFullscreen) ? forceMaxHeight : '' 
		}"
		:class="[
			'video-player-' + id, 
			{ 'auto-size' : autoSize && !isFullscreen }, 
			{ 'gif' : mediaType == 'gif' }
		]"
		ref="observe"
	 >
		<div v-show="isReady && hidePoster" style="overflow: hidden; border-radius: 6px;" @click="handleClick" class="uk-relative">
			
			<video 
				:id="'video-player-' + id" 
				ref="videoPlayer"
				class="video video-js skoften"
				>
			</video>
			
			<div class="uk-position-center" v-show="mediaType == 'gif' && isPaused">
				<div class="play-btn">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 26">
						<polygon class="play-btn__svg" points="9.33 6.69 9.33 19.39 19.3 13.04 9.33 6.69"/>
						<path class="play-btn__svg" d="M26,13A13,13,0,1,1,13,0,13,13,0,0,1,26,13ZM13,2.18A10.89,10.89,0,1,0,23.84,13.06,10.89,10.89,0,0,0,13,2.18Z"/>
					</svg> 		
				</div>	
			</div>

			<!-- explicit layer -->								          
			<div class="explicit-layer" v-show="explicit && !explicitHidden" @click="hideExplicitLayer">
				<div class="uk-position-center">
					<div class="uk-text-center">			
						<span class="icon"><i class="fal fa-exclamation-circle fa-3x"></i></span>
						<h4>Waarschuwing: expliciete content</h4>
						<p>klikken = kijken op eigen risico</p>
					</div>
				</div>
			</div>
						
		</div>		
		<div 
			v-show="!isReady || !hidePoster"
			class="poster uk-relative uk-text-center overflow-hidden" 
			>
				<img v-if="options.poster" :src="options.poster" :style="imageStyle"/>
				
				<span class="uk-position-center" v-show="!isReady">
						<loading-indicator></loading-indicator>
				</span>      
		</div>
		<div v-if="metaData != null && (hasProperties(metaData) || hasProperties(fallbackData)) && isRedactie && !isEmbed" class="meta-data uk-text-meta">
			<div class="uk-grid uk-margin-small-top">
				<div class="uk-width-auto" v-if="hasProperties(metaData)">
					Ratio: {{ getValue(metaData.width) }}x{{ getValue(metaData.height) }} 
					| Length: {{ Math.floor(metaData.length) }}s 
					| Size: {{ fileSizeInMB }}MB 
					| <a :href="originalUrl" target="_blank" v-show="originalUrl">Original</a> 
					| <a :href="encodedUrl" target="_blank" v-show="encodedUrl">Encoded</a> 
					| <a :href="streamUrl" target="_blank" v-show="streamUrl">Stream</a>
				</div>
				<div class="uk-width-expand uk-text-right" v-if="hasProperties(fallbackData)">
					<a :href="fallbackData.url" target="_blank" v-show="fallbackData.url">Fallback URL</a>
					<a href='' @click.prevent="toggleInfoBox(fallbackData.code)" v-show="fallbackData.code">Fallback CODE</a>
				</div>
			</div>
			<div v-show="fallbackInfo" class="uk-alert uk-alert-primary">{{ fallbackInfo }}</div>
		</div>
	</div>
</template>

<script>
import videojs from 'video.js'
import 'video.js/dist/video-js.css' 

// HLS support for old browsers
import '@videojs/http-streaming';

// Contrib ads
import 'videojs-contrib-ads'
import 'videojs-contrib-ads/dist/videojs-contrib-ads.css'

// Ima
import 'videojs-ima'
import 'videojs-ima/dist/videojs.ima.css'

// Mobile UI
import 'videojs-mobile-ui/dist/videojs-mobile-ui.css';
import 'videojs-mobile-ui';

// Other comppnents
import LoadingIndicator from './LoadingIndicator.vue'
import Cookies from 'js-cookie';

export default {
	name: 'VideoPlayer',
	components: {
		LoadingIndicator
	},
	props: {
		id: {
			default: Math.floor(Math.random() * 10000)
		},
		title: {
			type: String,
			default: '',
		},
		ads: {
			type: Object,
			default () {
				return {
					'website': false,
					'app': false
				};
			}
		},
		adsLimit: { // number of ads per 24 hour, where 0 is unlimited
			type: Object,
			default () {
				return {
					'website': 0,
					'app': 0
				};
			}
		},
		adsVdur: { // max time of ad
			type: Number,
			default: 60,
		},
		adsWaterfall: {
			type: Boolean,
			default: false
		},
		adsVastUrls: {
			type: Array,
			default: () => ([{
					'name': 'google',
					'url': 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=',
				},
				{
					'name': 'glomex',
					'url': 'https://glomex.github.io/vast-ima-player/linear-ad.xml',
				}
			])
		},
		analytics: {
			type: Boolean,
			default: false
		},
		consentCheck: {
			type: Boolean,
			required: false,
			default: true
		},
		playsInline: {
			type: Boolean,
			default: false
		},
		autoPlay: {
			type: Boolean,
			default: false
		},
		isEmbeddable: {
			type: Boolean,
			default: false
		},
		isEmbed: {
			type: Boolean,
			default: false
		},
		embedCode: {
			type: String,
			default: `<iframe src="https://www.site.com/video/id" width="560" height="315" frameborder="0"></iframe>`,
		},
		originalUrl: {
			type: String,
			default: null			
		},
		encodedUrl: {
			type: String,
			default: null
		},
		streamUrl: {
			type: String,
			default: null			
		},
		metaData: {
			type: Object,
			default: null,
		},
		options: {
			type: Object,
			default () {
				return {};
			}
		},
		viewportObserver: {
			type: Boolean,
			required: false,
			default: false,
		},
		viewport: {
			type: String,
			required: false
		},
		mediaType: { // mp4 or gif
			type: String,
		},
		autoSize: {
			type: Boolean,
			required: false,
			default: false
		},
		forceMaxHeight: {
			type: String,
			required: false,
		},
		fallbackData: {
			type: Object,
			default () {
				return {};
			}
		},
		mobileUi: {
			type: Boolean,
			default: false
		},
		explicit: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			observer: null,
			intersectionRatio: 0,
			player: null,
			shareModal: null,
			isReady: false,
			isVisible: null,
			isMuted: null,
			isPaused: null,
			isFullscreen: false,
			hidePoster: false,
			consentInterval: null,
			consentAttempts: 0,
			defaultOptions: {
				autoplay: false,
				preload: "auto",
				playsinline: true,
				crossorigin: "anonymous",
				controls: true,
				experimentalSvgIcons: true,
				poster: 'https://placehold.co/800x450?text=video',
				gifPoster: 'https://placehold.co/600x400?text=GIF',
				techOrder: ['html5'],
				controlBar: {
					fullscreenToggle: false
				},
			},
			adisReadyToStart: false,
			adIsPlaying: false,
			adCompleted: false,
			adSkipped: false,
			fallbackInfo: null,
			adsCurrentVastIndex: 0,
			userPressedPlay: null,
			explicitHidden: false,
		}
	},
	computed: {
		devMode() {
			return this.$root.devMode ?? false;
		},
		aspectRatio() {
			return (this.options.width / this.options.height) * 100;
		},
		posterStyle() {
			return {
				width: '100%',
				maxHeight: this.forceMaxHeight,
				paddingTop: `${this.aspectRatio}%`,
			};
		},
		imageStyle() {
			return {
				maxHeight: (this.forceMaxHeight && !this.isFullscreen) ? this.forceMaxHeight : '',
				height: 'auto',
			};
		},
		isRedactie() {
			return this.$root.user.isRedactie;
		},
		fileSizeInMB() {
			// Convert bytes to MB and fix to 2 decimal places
			return (this.metaData.filesize / 1024 / 1024).toFixed(2);
		},
		mergedOptions() {
			const defaultOptions = {
				autoplay: false,
				preload: "auto",
				playsinline: true,
				crossorigin: "anonymous",
				controls: true,
				experimentalSvgIcons: true,
				poster: 'https://placehold.co/800x450?text=video',
				gifPoster: 'https://placehold.co/600x400?text=GIF',
				techOrder: ['html5'],
				controlBar: {
					fullscreenToggle: false
				},
			};
			return {
				...defaultOptions,
				...this.options
			};
		}
	},
	mounted() {
		var app = this.$root;
		var component = this;
		var device = this.$root.device;

		// Add observer, this will init the video
		if(component.viewportObserver) {
			setTimeout(component.initObserver, 200);
		} else if(component.consentCheck) {
			component.startConsentCheck();
		} else {
			component.init();
		}

		// Set css variable for max height so all videjs elements are using it
		if(component.forceMaxHeight) {
			document.documentElement.style.setProperty('--forced-max-height', component.forceMaxHeight);
		}
	},
	beforeDestroy() {
		if(this.player) {
			this.player.dispose();
		}
		if(this.observer) {
			this.observer.disconnect();
		}
	},
	methods: {
		hideExplicitLayer: function(e) {
			this.explicitHidden = true;
		},
		getValue: function(value) {
			// Check if value is null or undefined
			return value == null ? 'null' : value;
		},
		hasProperties: function(object) {

			if(object == null) {
				return false;
			}

			if(JSON.stringify(object).length > 0) {
				return true;
			} else {
				return false;
			}
		},
		showCMP() {
			//this.$root.showCMP();
		},
		hasReachedAdCountLimit: function() {
			var component = this;
			var type = (this.$root.device.app) ? 'app' : 'website';
			let adCount = Cookies.get(`${type}AdCount`) || 0;

			if(component.adsLimit[type] === 0 || adCount < component.adsLimit[type]) {
				return false;
			} else {
				component.$logger('app/components/video-player/' + component.id + '/hasReachedAdCountLimit:true/' + component.id)
				return true;
			}
		},
		nextVastUrl: function() {

			var component = this;

			if(component.adsCurrentVastIndex < component.adsVastUrls.length - 1) {
				component.adsCurrentVastIndex++;
				var feed = component.adsVastUrls[component.adsCurrentVastIndex];

				component.$logger('app/components/video-player/' + component.id + '/nextVastUrl/index: ' + component.adsCurrentVastIndex + '/name: ' + feed.name);

				return feed.url;
			} else {
				return null; // No more ads to try
			}
		},
		init: function() {

			var component = this;

			// Merged options
			const mergedOptions = Object.assign({}, component.defaultOptions, component.options);
			component.$logger('app/components/video-player/' + component.id + '/init/' + component.id)

			// Create player instance
			component.player = videojs(component.$refs.videoPlayer, mergedOptions, () => {

				component.$logger('app/components/video-player/' + component.id + '/isReady')

				component.isReady = true;

				setTimeout(function() {
					component.hidePoster = true;
				}, (component.mediaType == 'gif') ? 0 : 500);

				// Muted?
				component.setMutedOrNot();

				// Start?
				if(component.isReady &&
					component.autoPlay &&
					!component.adIsPlaying &&
					component.isVisible) {
					component.$logger('app/components/video-player/' + component.id + '/autoplay')

					setTimeout(function() {
						component.player.play();
					}, 100);
				}
			});

			// VAST init
			if(
				(component.ads.website || component.ads.app) &&
				(!component.$root.user.loggedIn || component.$root.user.isRedactie) &&
				!component.hasReachedAdCountLimit() &&
				typeof google != "undefined" &&
				typeof component.player.ima != "undefined"
			) {
				component.$logger('app/components/video-player/' + component.id + '/ads-enabled')
				component.initIMA();
			}

			// Add event listeners
			component.player.on('pause', component.handlePause);
			component.player.on('volumechange', component.handleVolumeChange);
			component.player.on('play', component.handlePlay);
			component.player.on('ended', component.handlePlay);
			component.player.on('fullscreenchange', component.handleFullscreen);

			// Track manual play event
			component.player.bigPlayButton.on('click', function() {
				component.userPressedPlay = true;
			});

			// Add custom fullscreen icon so we can handle it our selves (required for android vs ios)
			component.addCustomFullScreenButton();

			// Add embed button
			if(component.isEmbeddable) {
				component.addEmbedButton();
			}

			// Mobile UI plugin
			if(component.mobileUi) {
				component.player.mobileUi({
					fullscreen: {
						enterOnRotate: true,
						exitOnRotate: true,
						lockOnRotate: true,
						lockToLandscapeOnEnter: false,
						disabled: false
					},
					touchControls: {
						seekSeconds: 10,
						tapTimeout: 300,
						disableOnEnd: false,
						disabled: false,
					}
				});
			}
		},
		debounce: function(func, delay) {
			let timer;
			return function() {
				const context = this,
					args = arguments;
				clearTimeout(timer);
				timer = setTimeout(() => func.apply(context, args), delay);
			};
		},
		initObserver: function() {

			var component = this;
			let count;
			var lastTimeout;

			component.$logger('app/components/video-player/init-observer/' + component.id);

			var options = {
				root: null,
				rootMargin: '-45% 0% -45% 0%',
				threshold: 0
			};

			if(component.mediaType == "gif") {
				options.rootMargin = '20% 0% 20% 0%';
			}

			var elementToObserve = this.$refs.observe;

			const observerCallback = (entries) => {
				entries.forEach(entry => {

					component.intersectionRatio = entry.intersectionRatio;

					// In viewport
					if(entry.isIntersecting) {

						component.$logger('app/components/video-player/observer/visible/' + component.id);

						component.isVisible = true;

						// Not isReady
						if(!component.isReady) {

							// Consent check   
							if(component.ads &&
								component.consentCheck &&
								typeof google != 'undefined') {
								// If consent fails, it will init the player
								component.startConsentCheck();
							} else {
								component.init();
							}

							// Player isReady
						} else {

							// Mute or not, depends on some variables and devices settings
							component.setMutedOrNot();

							if(component.autoPlay &&
								!component.adIsPlaying &&
								component.isVisible) {
								component.$logger('app/components/video-player/' + component.id + '/autoplay')

								setTimeout(function() {
									component.player.play();
								}, 250);
							}

						}

						// Out of viewport
					} else {

						component.isVisible = false;
						component.$logger('app/components/video-player/observer/invisible/' + component.id);

						if(component.isReady) {
							component.player.pause();
						}
					}

				});
			};

			// Wrap the observer callback with debounce
			const debounceDelay = (component.mediaType == 'gif') ? 50 : 250;
			const debouncedObserverCallback = this.debounce(observerCallback, debounceDelay);

			component.observer = new IntersectionObserver(debouncedObserverCallback, options);

			component.observer.observe(elementToObserve);

		},
		initIMA: function() {

			var component = this;

			if(typeof component.player.ima == "undefined") {
				return;
			}

			// Initial VAST Url
			var initialIndex = 0;
			if(component.$helper.getURLParameter('vastIndex') != null) {
				initialIndex = component.$helper.getURLParameter('vastIndex');
				component.adsCurrentVastIndex = initialIndex;
			}

			var initialAdUrl = component.adsVastUrls[initialIndex].url;
			initialAdUrl = component.addExtraParameters(initialAdUrl);

			// Ima options
			var imaOptions = {
				adTagUrl: initialAdUrl,
				disableAdControls: false,
				adLabel: component.adLabel ?? 'ad',
				numRedirects: 10,
				debug: component.devMode
			};

			// Ad event: adsisReady
			component.player.on("adsready", function(e) {

				component.$logger('app/components/video-player/' + component.id + '/ads/isReady (feed: ' + component.adsVastUrls[component.adsCurrentVastIndex].name + ')');

				component.adisReadyToStart = true;

				component.player.ima.addEventListener(google.ima.AdEvent.Type.LOADED, function(event) {
					var ad = event.getAd();
					var adSystem = ad.getAdSystem();
					var clickThroughUrl = ad.data.clickThroughUrl;

					component.$logger('app/components/video-player/' + component.id + '/ads/loaded/adSystem/' + adSystem);
					component.$logger('app/components/video-player/' + component.id + '/ads/loaded/clickUrl/' + clickThroughUrl);
					component.$logger(JSON.stringify(ad));
				});

				// Ad event: started
				component.player.ima.addEventListener(google.ima.AdEvent.Type.STARTED, function(event) {

					component.$logger('app/components/video-player/' + component.id + '/ads/started');
					component.$logger(event);

					// Playing state
					component.adIsPlaying = true;

					// Update cookie ad count for app or website
					var type = (component.$root.device.app) ? 'app' : 'website';
					let adCount = Cookies.get(`${type}AdCount`) || 0;
					
					// set cookie duration to 4 hours
					const fourHoursInMilliseconds = 4 * 60 * 60 * 1000;
					
					Cookies.set(`${type}AdCount`, ++adCount, {
						expires: fourHoursInMilliseconds
					});

					// Tracking
					if(component.analytics) {

						var ad = event.getAd();
						var adSystem = (ad) ? ad.getAdSystem() : 'Unknown';

						// Send event to Google Analytics
						gtag('event', 'video_ad_play', {
							'event_category': 'Ads',
							'event_label': adSystem,
							'ad_system': adSystem,
							'document_url': document.location.href,
							'advertiser_name': ad.getAdvertiserName()
						});
					}

				});

				// Ad event: ended/completed
				component.player.ima.addEventListener(google.ima.AdEvent.Type.COMPLETE, function() {
					component.$logger('app/components/video-player/' + component.id + '/ads/completed');
					component.adIsPlaying = false;
					component.adCompleted = true;
				});

				// Ad event: skipped
				component.player.ima.addEventListener(google.ima.AdEvent.Type.SKIPPED, function() {
					component.$logger('app/components/video-player/' + component.id + '/ads/skipped');
					component.adIsPlaying = false;
					component.adSkipped = true;
				});
			});

			// Ad event: adsError
			component.player.on("adserror", function(e) {

				component.$logger('app/components/video-player/' + component.id + '/ads/error: ' + e.data.AdError.data.errorMessage);
				component.$logger('app/components/video-player/' + component.id + '/ads/error/feed: ' + component.adsVastUrls[component.adsCurrentVastIndex].name + ' has no valid response');
				component.$logger(e);

				// Get next vast url
				if(component.adsWaterfall && component.$helper.getURLParameter('vastIndex') == null) {
					var nextUrl = component.nextVastUrl();
					if(nextUrl) {

						component.$logger('app/components/video-player/' + component.id + '/ads/changeAdTag/url: ' + nextUrl);

						nextUrl = component.addExtraParameters(nextUrl);
						component.player.ima.changeAdTag(nextUrl);
						component.player.ima.requestAds();

					} else {
						component.$logger('app/components/video-player/' + component.id + '/ads/error/all-feeds-failed-to-deliver');
					}
				}
			});

			// Init ima	
			component.player.ima(imaOptions);

			// Remove controls from the player on iPad to stop native controls from stealing
			// our click
			var contentPlayer = document.getElementById('video-player-' + component.id + '_html5_api');
			if((navigator.userAgent.match(/iPad/i) ||
				navigator.userAgent.match(/Android/i)) &&
				contentPlayer.hasAttribute('controls')) {
				contentPlayer.removeAttribute('controls');
			}

			// Initialize the ad container when the video player is clicked, but only the
			// first time it's clicked.
			var initAdDisplayContainer = function() {
				component.player.ima.initializeAdDisplayContainer();
				wrapperDiv.removeEventListener(startEvent, initAdDisplayContainer);
			}

			var startEvent = 'click';
			if(navigator.userAgent.match(/iPhone/i) ||
				navigator.userAgent.match(/iPad/i) ||
				navigator.userAgent.match(/Android/i)) {
				startEvent = 'touchend';
			}

			var wrapperDiv = document.getElementById('video-player-' + component.id);
			wrapperDiv.addEventListener(startEvent, initAdDisplayContainer);

		},
		setMutedOrNot: function() {

			// Mute when user has manually muted a player
			// or when we're on mobile, because mobile won't allow autoplay with sound
			// or when where's playing a ad and user has not interacted with the DOM

			var component = this;

			if(component.options.muted) {
				component.player.muted(true);
			} else if(Cookies.get('videoPlayerMuted') == 'true' && component.userPressedPlay == false) {
				component.player.muted(true);
			} else if(component.$root.device.mobile && component.autoPlay) {
				component.player.muted(true);
			} else if(!window.userInteracted && component.autoPlay) {
				component.player.muted(true);
			} else if(!window.userInteracted && component.adisReadyToStart) {
				component.player.muted(true);
			} else {
				component.player.muted(false);
			}
		},
		canAutoPlay: function(string) {
			var component = this;
			var promise = component.player.play();

			if(promise !== undefined) {
				promise.then(function() {}).catch(function(error) {});
			}
		},
		handleClick() {
			var component = this;
			if(component.mediaType == "gif") {
				if(component.isPaused) {
					component.player.play();
				} else {
					component.player.pause();
				}
			}
		},
		handleFullscreen: function(event) {
			var component = this;
			this.$logger('app/component/video-player/' + component.id + '/handleFullscreen');

			// Disable forceMaxHeight when people click fullscreen
			if(this.player.isFullscreen()) {
				this.isFullscreen = true;
			} else {
				this.isFullscreen = false;
			}
		},
		handleVolumeChange: function() {
			this.isMuted = this.player.muted();
			// Store in cookie so we can check it on other videoplayer in the page
			Cookies.set('videoPlayerMuted', (this.isMuted) ? 'true' : 'false', {
				expires: 365
			});
		},
		handlePause: function() {
			this.$logger('app/components/video-player/' + this.id + '/event:pause');
			this.isPaused = true;
		},
		handlePlay: function() {

			var component = this;

			if(component.ads &&
				component.adsCurrentVastIndex > 0 &&
				!component.adCompleted &&
				!component.adSkipped
			) {
				component.player.ima.requestAds();
			}

			this.$logger('app/components/video-player/' + this.id + '/event:play');
			this.isPaused = false;

			// Track event
			if(this.analytics) {
				//component.trackEvent('play');

				gtag('event', 'video_play', {
					'event_category': 'Video',
					'event_label': component.title,
					'video_id': component.id,
					'document_url': document.location.href
				});

			}
		},
		handleEnded: function() {
			this.$logger('app/components/video-player/' + this.id + '/event:ended');

			// Track event
			if(this.analytics) {
				//component.trackEvent('completed');
			}
		},
		trackEvent: function(eventName) {

			if(typeof gtag == "undefined") return;

			var component = this;

			var trackingData = {
				id: component.id,
				event: eventName,
				url: window.location.href,
				title: component.title
			};

			gtag('event', 'videos', trackingData);

			component.$logger('app/components/video-player/track:' + eventName + '/' + component.id);
		},
		addEmbedButton: function() {

			var component = this;

			// Create Button 
			const myButton = component.player.getChild('ControlBar').addChild('button', {
				controlText: '',
				className: 'vjs-visible-text',
				clickHandler: function(event) {

					// Open Modal        
					component.shareModal.open();

					// Attach click listener so we can copy the code to clipboard
					var embedButton = document.getElementById('video-embed-button-' + component.id);
					var embedCode = document.getElementById('video-embed-code-' + component.id);

					embedButton.addEventListener('click', () => {
						embedCode.select();
						document.execCommand('copy');
					});
				}
			});

			// Set icon
			myButton.setIcon('share');

			// Create Modal overlay
			const ModalDialog = videojs.getComponent('ModalDialog');

			var modalHtmlContent = document.createElement('div');
			modalHtmlContent.innerHTML = `
				<div class="vjs-embed-modal uk-padding">
		  		<div class="uk-position-center">
					<h4 style="color: white">Video embedden</h4>
					<div class="uk-grid uk-grid-small">
			  		<div class="uk-width-3-4">
						<input type="text" class="uk-input uk-width-1-1" id="video-embed-code-${component.id}" value='${component.embedCode}'>
			  		</div>
			  		<div class="uk-width-expand">
						<a class="uk-button uk-button-primary" id="video-embed-button-${component.id}">kopiëren</a>
			  		</div>
					</div>
		  		</div>
				</div>
	  		`;

			component.shareModal = new ModalDialog(component.player, {
				temporary: false,
				content: modalHtmlContent
			});

			component.player.addChild(component.shareModal);

		},
		addCustomFullScreenButton: function() {

			var component = this;
			
			// Create Button 
			const myButton = component.player.getChild('ControlBar').addChild('button', {
				controlText: '',
				className: 'vjs-visible-text',
				clickHandler: function(event) {

					var currentTime = component.player.currentTime() || 0;

					// For android, we are showing a native screen with videoplayer
					// Reason: https://github.com/flutter/flutter/issues/139971#issuecomment-1851864013
					if(component.$root.device.app && component.$root.device.android) {
						component.$helper.sendAppMessage({
							'action': 'showVideoPlayer',
							'fileData': {
								'url': component.encodedUrl,
								'currentTime': currentTime
							}
						});
					} else {
						
						// iOS & web
						if(component.player.isFullscreen()) {
							component.player.exitFullscreen();
						} else {
							component.player.requestFullscreen();
						}
					}
				}
			});

			// Set icon
			myButton.setIcon('fullscreen-enter');
		},
		toggleInfoBox: function(content) {
			this.fallbackInfo = content;
		},
		getWeightedVastUrl: function() {
			var component = this;
			let totalWeight = 0;
			for(const url in component.adsVastUrls) {
				totalWeight += component.adsVastUrls[url].weight;
			}

			let randomNum = Math.random() * totalWeight;

			for(const url in component.adsVastUrls) {
				randomNum -= component.adsVastUrls[url].weight;
				if(randomNum <= 0) {
					return component.adsVastUrls[url].url;
				}
			}
		},
		addExtraParameters: function(url) {

			var app = this.$root;
			var device = app.device;
			var component = this;
			var adUrl = url;

			if(!url) {
				return;
			}

			// Set default parameters
			var csid = 'skoften_desktop';
			var consentString = app.consent;
			var pvrn = Math.floor(Math.random() * 100000000);
			var vprn = Math.floor(Math.random() * 1000000000)
			var vdur = component.adsVdur;

			// Update csid based on platform type
			if(device.app) {
				csid = (device.android) ? 'skoften_inapp' : 'skoften_inapp';
			} else if(device.mobile) {
				csid = (device.android) ? 'skoften_mweb' : 'skoften_mweb';
			}

			// Extract query parameters from adUrl
			var urlParts = adUrl.split('?');
			var baseUrl = urlParts[0];
			var searchParams = new URLSearchParams(urlParts[1] || '');
			
			// Only add the csid when it's not set in video.twig
			if (!searchParams.has('csid')) {
				searchParams.set('csid', csid);
			}
			
			// Update query params 
			//adUrl = component.$helper.updateQueryStringParameter(adUrl, 'csid', csid);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'pvrn', pvrn);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vprn', vprn);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vdur', vdur);

			// Replace with consent			
			adUrl = adUrl.replace('%_FW_GDPR_CONSENT%', consentString);
			adUrl = adUrl.replace('%_FW_GDPR_CONSENT_A%', consentString);
			adUrl = adUrl.replace('%_FW_GDPR_CONSENT_B%', consentString);
			adUrl = adUrl.replace('%_FW_GDPR_CONSENT_C%', consentString);

			// Custom VAST waterfall, add consent, provider
			//if(component.$helper.getURLParameter('waterfall') == 'true') {
			if(adUrl.includes('www.skoften.net') || adUrl.includes('dev.skoften.net')) {
				adUrl = component.$helper.updateQueryStringParameter(adUrl, 'consent', consentString);
			}

			component.$logger('app/components/video-player/' + component.id + '/update-ad-url: ' + adUrl);

			return adUrl;
		},
		startConsentCheck: function() {

			var app = this.$root;
			var component = this;
			var maxChecks = (component.isEmbed == true) ? 0 : 10; // stop check for embed codes

			component.$logger('app/components/video-player/' + component.id + '/start-consent-check/max-check: ' + maxChecks);

			// Set up polling every second
			component.consentInterval = setInterval(() => {

				component.consentAttempts++;

				if(app.consent && app.consent.length > 100) {

					// Init player
					component.init();

					// Clear interval
					component.clearConsentInterval();

				} else if(component.consentAttempts >= maxChecks) {

					// Give up consent, init player
					component.init();

					// Clear interval
					component.clearConsentInterval();

					// reset attempts		
					component.consentAttempts = 0;
				}
			}, 500);

		},
		clearConsentInterval: function() {
			var component = this;
			clearInterval(component.consentInterval);
			component.consentInterval = null;
		},
	}
}
</script>

<style>
/* player component */
.component.video-player {
	overflow: hidden;
	min-height: 180px;
}
.component.video-player .video {
	background: #20202C;
	border-radius: 8px;
	overflow: hidden;
}
.component.video-player .poster {
	min-height: 180px; 
	max-width: 100%; 
	overflow: hidden;
	position: relative;
	background: #20202C;
	border-radius: 8px;	
	margin: auto;
}

/* --- play icon --- */
.play-btn {
	width: 80px;
	height: auto;
	margin: 0 auto;
	margin-top: 0;
}

.play-btn__svg{
	transition: 0.3s; 
	fill:#fff; 
	opacity: 0.8;
}

.play-btn:hover .play-btn__svg {
	fill: #ff3d00;
	opacity: 0.9;
}

/* --- videojs custom --- */
.video-js {
	border-radius: 10px !important;
	font-size: 0.8rem !important;
	width: 100% !important;
}
.video-js .vjs-tech {
	border-radius: 8px !important;
	overflow: hidden;
}

/* progress ad bar and volume color */
.video-js .ima-ad-container .ima-controls-div .ima-progress-div,
.video-js .ima-slider-level-div {
	background-color: var(--tint-color) !important;
}

/* Make the video relative, instead of absolute, so that
	 the parent container will size based on the video. Also,
	 note the max-height rule. Note the line-height 0 is to prevent
	 a small artifact on the bottom of the video.
 */
.auto-size .video-js.vjs-fluid,
.auto-size .video-js.vjs-16-9,
.auto-size .video-js.vjs-4-3,
.auto-size .video-js video.video-js,
.auto-size .video-js video.vjs-tech,
.auto-size .video-js .vjs-tech {
	position: relative !important;
	/*max-height: 450px !important;*/
	max-height: var(--forced-max-height) !important;
	width: 100% !important;
	height: auto !important;
	max-width: 100% !important;
	padding-top: 0 !important;
	line-height: 0 !important;
}

/* Fix the control bar due to us resetting the line-height on the video-js */
.auto-size .video-js .ima-ad-container .ima-controls-div {
	overflow: unset;
}
.auto-size .vjs-control-bar {
	line-height: 1 !important;
}

.video-js .vjs-progress-control .vjs-play-progress {
	background-color: var(--tint-color) !important;
}
.video-js .vjs-big-play-button {
	font-size: 2rem !important;
	line-height: 2rem !important;
	margin-top: -2.4rem !important;
	border: 0 !important;
	width: 5rem !important;
	height: 5rem !important;
	background-color: var(--tint-color) !important;
	border-radius: 2.5rem !important;
	background-color: var(--tint-color) !important;
}
.vjs-big-play-button .vjs-svg-icon {
	width: 2.2rem  !important;
	height: 2.2rem !important;
}

/* IMA */
.component.video-player .video-js .ima-ad-container .ima-controls-div {
	overflow: unset;
}
.component.video-player .video-js .ima-ad-container .ima-controls-div .ima-progress-div {
	background-color: #ff3d00 !important;
}
.ima-countdown-div {
	padding-left: 4px !important;
	padding-bottom: 4px !important;
}


/* ---explicit layer--- */
.component.video-player .explicit-layer {
	cursor: pointer;
	position: absolute;
	height: 100%;
	width: 100%;
	top: 0;
	bottom: 0;
	background-color:rgba(0, 0, 0, 0.9);
	color: white;
}

.component.video-player .explicit-layer i {
	color: var(--tint-color);
}
.component.video-player .explicit-layer h4 {
	color: white;
}
</style>